import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import PagedPdfViewer from "@/components/PagedPdfViewer";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { Loader2, FileText, Eye, Code2, Copy, FileCode2, Save } from "lucide-react";

async function downloadBlob(url, filename) {
  const r = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = href; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(href);
}

const escapeXml = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// XML für die Anzeige einrücken (flache juris-vkblatt-Struktur; Volltext bleibt als Block erhalten).
function prettyXml(src) {
  if (!src) return "";
  try {
    const doc = new DOMParser().parseFromString(src, "application/xml");
    if (doc.getElementsByTagName("parsererror").length) return src;
    const out = [];
    const walk = (node, depth) => {
      if (node.nodeType !== 1) return;
      const pad = "  ".repeat(depth);
      const attrs = Array.from(node.attributes).map((a) => ` ${a.name}="${a.value}"`).join("");
      const elemChildren = Array.from(node.childNodes).filter((c) => c.nodeType === 1);
      if (elemChildren.length === 0) {
        const text = node.textContent || "";
        if (text.includes("\n")) {
          out.push(`${pad}<${node.nodeName}${attrs}>`);
          out.push(escapeXml(text));
          out.push(`${pad}</${node.nodeName}>`);
        } else {
          out.push(`${pad}<${node.nodeName}${attrs}>${escapeXml(text)}</${node.nodeName}>`);
        }
      } else {
        out.push(`${pad}<${node.nodeName}${attrs}>`);
        elemChildren.forEach((c) => walk(c, depth + 1));
        out.push(`${pad}</${node.nodeName}>`);
      }
    };
    walk(doc.documentElement, 0);
    return '<?xml version="1.0"?>\n' + out.join("\n") + "\n";
  } catch { return src; }
}

const KIND_LABEL = { gesamtausgabe: "Gesamtausgabe", inhaltsverzeichnis: "Inhaltsverzeichnis", entry: "Eintrag" };

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</div>
      <div className="text-sm text-gray-900 font-medium break-words">{value || "—"}</div>
    </div>
  );
}

// OK-Splitdokument: Layout wie PP — Mitte Editor(Details)/XML per Umschalter, PDF rechts.
export default function OkDocViewer({ okId, actions }) {
  const isDesktop = useIsDesktop();
  const [doc, setDoc] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [tab, setTab] = useState("editor");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setDoc(null); setTab("editor");
    api.get(`/ok-documents/${okId}`).then((r) => {
      if (!active) return;
      setDoc(r.data);
      setTitle(r.data.drstitel || "");
    }).catch(() => toast.error("OK-Dokument nicht gefunden"));
    return () => { active = false; };
  }, [okId]);

  useEffect(() => {
    let url = null; let cancelled = false;
    setPdfUrl(null); setPdfError(false);
    (async () => {
      try {
        const r = await api.get(`/ok-documents/${okId}/pdf`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setPdfUrl(url);
      } catch { if (!cancelled) setPdfError(true); }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [okId]);

  if (!doc) return <div className="h-full flex items-center justify-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const xmlPretty = prettyXml(doc.xml || "");
  const titleDirty = (title || "") !== (doc.drstitel || "");

  const saveTitle = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/ok-documents/${okId}`, { drstitel: title });
      setDoc(r.data);
      setTitle(r.data.drstitel || "");
      toast.success("Titel gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* toolbar */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-5 py-3 flex items-center gap-3 flex-wrap">
        <div className="flex rounded-md border border-gray-300 overflow-hidden">
          <button onClick={() => setTab("editor")} data-testid="ok-tab-editor" title="Editor-Ansicht"
            className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${tab === "editor" ? "bg-[#0F4C81] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
            <Eye className="h-3.5 w-3.5" /> Editor
          </button>
          <button onClick={() => setTab("xml")} data-testid="ok-tab-xml" title="XML-Ansicht"
            className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${tab === "xml" ? "bg-[#0F4C81] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
            <Code2 className="h-3.5 w-3.5" /> XML
          </button>
        </div>
        <Button variant="outline" size="sm" onClick={() => downloadBlob(`/ok-documents/${okId}/export/xml`, `${doc.pdfdatei}.xml`)} data-testid="ok-dl-xml" title="XML herunterladen"><FileCode2 className="h-4 w-4 mr-1" /> XML</Button>
        <Button variant="outline" size="sm" onClick={() => downloadBlob(`/ok-documents/${okId}/export/pdf`, doc.filename)} data-testid="ok-dl-pdf" title="PDF herunterladen"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
        {actions ? <div className="flex items-center gap-2 flex-wrap">{actions}</div> : null}
        <div className="hidden lg:block flex-1" />
        <div className="min-w-0 text-right">
          <div className="flex items-center gap-2 justify-end">
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-300">{KIND_LABEL[doc.kind] || "Eintrag"}</span>
            <h2 className="font-mono text-sm font-bold text-gray-900 truncate" data-testid="ok-filename">{doc.filename}</h2>
            <StatusBadge status={doc.qk_status || "offen"} testId="ok-qk-status" />
          </div>
          <p className="text-xs text-gray-500 truncate" data-testid="ok-drstitel">{doc.drstitel} · S.&nbsp;{doc.vonseite}–{doc.bisseite}</p>
        </div>
      </div>

      {/* content: Mitte (Editor/XML) + PDF rechts */}
      <div className="flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-2 overflow-y-auto lg:overflow-hidden">
        {/* MITTE */}
        {tab === "xml" ? (
          <div className="shrink-0 min-h-[70vh] lg:min-h-0 overflow-hidden flex flex-col lg:border-r border-gray-300">
            <div className="shrink-0 px-5 py-2 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-500">juris-vkblatt-XML (mit Volltext)</span>
              <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(xmlPretty); toast.success("XML kopiert"); }} data-testid="ok-xml-copy" title="XML in Zwischenablage kopieren"><Copy className="h-3.5 w-3.5 mr-1" /> Kopieren</Button>
            </div>
            <textarea readOnly value={xmlPretty} spellCheck={false}
              className="flex-1 min-h-0 w-full resize-none overflow-auto bg-gray-900 text-gray-100 text-xs p-5 font-mono leading-relaxed outline-none border-0"
              data-testid="ok-xml-viewer" />
          </div>
        ) : (
          <div className="shrink-0 min-h-0 lg:overflow-y-auto bg-gray-100 lg:border-r border-gray-300 p-5 space-y-5" data-testid="ok-editor-view">
            {/* Makrolog-Daten (Heft-Metadaten) */}
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#0F4C81] mb-3">Heft-Daten</div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Bundesland" value={(doc.land || "").toUpperCase()} />
                <Field label="Blatt" value={(doc.blatt || "").toUpperCase()} />
                <Field label="Heftnummer" value={doc.nummer} />
                <Field label="Datum" value={doc.vkdatum} />
                <div className="col-span-2"><Field label="Dateiname" value={doc.filename} /></div>
              </div>
            </div>

            {/* Trennlinie */}
            <div className="border-t-2 border-gray-300" />

            {/* Eintragsdaten */}
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#0F4C81] mb-3">Eintragsdaten</div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Von Seite" value={doc.vonseite} />
                <Field label="Position" value={doc.vonzusatz} />
                <Field label="Bis Seite" value={doc.bisseite} />
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Titel</label>
                  <Button size="sm" onClick={saveTitle} disabled={!titleDirty || saving}
                    className="bg-[#0F4C81] hover:bg-[#0d3f6c] h-7 px-3 text-xs" data-testid="ok-save-title" title="Titel speichern">
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />} Titel speichern
                  </Button>
                </div>
                <Textarea value={title} onChange={(e) => setTitle(e.target.value)} rows={3}
                  className="bg-white border-gray-400 text-gray-900 text-sm resize-none"
                  data-testid="ok-title-input" />
              </div>
            </div>
          </div>
        )}

        {/* PDF RECHTS. Auf Mobil/iPad Bild-Viewer (blätterbar + Seitenanzahl). */}
        <div className="shrink-0 h-[80vh] lg:h-full lg:min-h-0 bg-gray-200" data-testid="ok-pdf-viewer">
          {!isDesktop ? (
            <PagedPdfViewer basePath={`/ok-documents/${okId}`} />
          ) : pdfUrl ? (
            <iframe title="PDF-Ansicht" src={`${pdfUrl}#zoom=page-width`} className="w-full h-full border-0" data-testid="ok-pdf-frame" />
          ) : pdfError ? (
            <div className="h-full flex items-center justify-center text-center p-6 text-gray-500">
              <div><FileText className="h-6 w-6 mx-auto" /><p className="mt-2 text-xs">PDF nicht verfügbar.</p></div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /><p className="mt-2 text-xs">PDF wird geladen…</p></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
